import React, { Fragment, useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { RequestStatus } from '@atlassian/navigation-bff-data-provider';
import {
	ERROR_LOCATION__GLOBAL_SHORTCUTS,
	NavErrorBoundary,
} from '@atlassian/navigation-error-boundary';

import { ANALYTICS_CHANNEL } from '../../constants';
import type {
	GlobalShortcutComponentItemBase,
	GlobalShortcutExpandItem,
	GlobalShortcutLinkItem,
	MappedGlobalShortcutsItem,
} from '../../types';
import { getFallbackUrlMaybe } from '../../utils/get-fallback-url-maybe';
import { getUrlMaybe } from '../../utils/get-url-maybe';
import { MenuComponentItem } from '../menu-component-item';
import { MenuExpandItem } from '../menu-expand-item';
import { fireMenuLinkItemClickAnalyticsEvent, MenuLinkItem } from '../menu-link-item';

import { messages } from './messages';

export type ShortcutMenuItemProps = {
	// This component is exported from the package, which means we can't rely on the context provider that Global Shortcuts uses to determine if Drag & Drop is enabled, as consumers might be using something else.
	// Thus, this value needs to be provided as a prop by whatever is rendering it.
	dragAndDropEnabled?: boolean;
	expandItems?: (GlobalShortcutExpandItem | GlobalShortcutLinkItem)[];
	forceStatic?: boolean;
	isInMoreMenu?: boolean;
	openInSameTab?: boolean;
	product?: string;
	promotedItem?: GlobalShortcutExpandItem | GlobalShortcutLinkItem;
	shortcut: MappedGlobalShortcutsItem;
	siteName?: string;
	status?: RequestStatus | null;
};

export function ShortcutMenuItem({
	shortcut,
	dragAndDropEnabled,
	status,
	expandItems,
	forceStatic,
	promotedItem,
	siteName,
	product,
	openInSameTab,
	isInMoreMenu,
}: ShortcutMenuItemProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsName = shortcut.analyticsName || `unknown_${shortcut.label}`;
	const ItemWrapper = shortcut?.ItemWrapper ?? React.Fragment;

	const siteNameAriaLabel = siteName ? ` (${siteName})` : '';

	// Memoize the error boundary location to avoid recreating it on every render
	const boundaryLocation = useMemo(() => shortcut.key, [shortcut.key]);

	/**
	 * If the data provider is being initialised or loading, we want to display a placeholder item with a spinner.
	 */
	const loading = useMemo(
		() =>
			!!(
				!forceStatic &&
				status &&
				(status === RequestStatus.INIT || status === RequestStatus.LOADING)
			),
		[status, forceStatic],
	);

	/**
	 * Set disabled state based on what we know at this point
	 * from whether we are forcing static rendering and the status of the BFF request.
	 * If we have more conditions received from the BFF in the future (such as permissions), we can account for those here too.
	 */
	const disabled = useMemo(() => {
		// If forceStatic is explicitly set to true, do not disable the item
		if (forceStatic) {
			return false;
		}

		// Do not explicitly disable links that have empty URLs, because we don't want to display them at all
		// todo: Should we include undefined URLs and fallback URLs?
		if (shortcut.type === 'link') {
			if (getFallbackUrlMaybe(shortcut.fallbackUrl) !== undefined) {
				return false;
			}
		}

		// Only show disabled state when the BFF request is complete and fails
		if (status === RequestStatus.ERROR) {
			return true;
		}

		if (forceStatic === undefined) {
			return false;
		}

		return !forceStatic;
	}, [forceStatic, shortcut, status]);

	/**
	 * An event to pass down to the inner menu item component (MenuLinkItem or MenuExpandItem) to be called when the item is disabled.
	 * This allows the inner components to handle further disabling conditions within themselves if applicable,
	 * and optionally pass up a reason for the disabled state determined within itself.
	 * If no reason is passed, we check against what we know here from the data received from the BFF.
	 */
	const onDisable = useCallback(
		(reason?: string) => {
			// Do not fire analytics event until loading is complete
			if (!status || status === RequestStatus.INIT || status === RequestStatus.LOADING) {
				return;
			}

			// This currently covers reasons like request errors.
			// If the BFF gives us more information in the future (such as permissions), that should be accounted for here too.
			const bffReason = status !== RequestStatus.COMPLETE ? status : undefined;

			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'disabled',
				actionSubject: 'button',
				actionSubjectId: 'globalNavigation',
				attributes: {
					name: analyticsName,
					reason: reason ?? bffReason,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, analyticsName, status],
	);

	if (shortcut.type === 'component') {
		if (fg('navx-3539-custom-shortcut-component-draggable')) {
			return (
				<ShortcutBoundary location={boundaryLocation} product={product}>
					<MenuComponentItem
						shortcut={shortcut}
						dragAndDropEnabled={dragAndDropEnabled}
						isInMoreMenu={isInMoreMenu}
						onClick={() => {
							fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
						}}
					/>
				</ShortcutBoundary>
			);
		}

		const Component = shortcut.component as GlobalShortcutComponentItemBase['component'];
		return (
			<ShortcutBoundary location={boundaryLocation} product={product}>
				<ItemWrapper>
					<Component
						onClick={() => {
							fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
							shortcut.onClick?.();
						}}
						isInMoreMenu={isInMoreMenu}
					/>
				</ItemWrapper>
			</ShortcutBoundary>
		);
	}

	if (shortcut.type === 'expand') {
		return (
			<ShortcutBoundary location={boundaryLocation} product={product}>
				<ItemWrapper>
					<MenuExpandItem
						optionalRef={shortcut.ref}
						key={shortcut.key}
						analyticsName={analyticsName}
						icon={shortcut.icon}
						label={shortcut.label}
						labelElem={shortcut.labelElem}
						items={expandItems}
						disabled={disabled}
						onDisable={onDisable}
						promotedItem={promotedItem}
						loading={loading}
						onClick={shortcut.onClick}
						onRightClick={shortcut.onRightClick}
						product={product}
					/>
				</ItemWrapper>
			</ShortcutBoundary>
		);
	}

	if (shortcut.type === 'internal') {
		return (
			<ShortcutBoundary location={boundaryLocation} product={product}>
				<ItemWrapper>
					<MenuLinkItem
						analyticsName={analyticsName}
						key={shortcut.key}
						disabled={disabled}
						icon={shortcut.icon}
						url={getUrlMaybe(shortcut)}
						label={shortcut.label}
						labelElem={shortcut.labelElem}
						onDisable={onDisable}
						loading={loading}
						isReorderable={shortcut.isReorderable}
						openInSameTab={true}
						onClick={shortcut.onClick}
						onRightClick={shortcut.onRightClick}
						product={product}
						isSelected={shortcut.isSelected}
					/>
				</ItemWrapper>
			</ShortcutBoundary>
		);
	}

	return (
		<ShortcutBoundary location={boundaryLocation} product={product}>
			<ItemWrapper>
				<MenuLinkItem
					optionalRef={shortcut.ref}
					analyticsName={analyticsName}
					key={shortcut.key}
					disabled={disabled}
					apsKey={shortcut.apsKey}
					icon={shortcut.icon}
					url={getUrlMaybe(shortcut)}
					label={shortcut.label}
					labelElem={shortcut.labelElem}
					onDisable={onDisable}
					loading={loading}
					actions={shortcut.actions}
					ariaLabel={formatMessage(messages.menuLinkItemAriaLabel, {
						appName: shortcut.label,
						siteName: siteNameAriaLabel,
					})}
					onClick={shortcut.onClick}
					isReorderable={shortcut.isReorderable}
					onRightClick={shortcut.onRightClick}
					onMouseEnter={shortcut.onMouseEnter}
					onFocus={shortcut.onFocus}
					menuItemId={shortcut.menuItemId}
					product={product}
					openInSameTab={openInSameTab}
					optionsMenuElem={shortcut.optionsMenuElem}
				/>
			</ItemWrapper>
		</ShortcutBoundary>
	);
}

const ShortcutBoundary = React.memo(function ShortcutBoundary({
	location,
	product,
	children,
}: {
	children: React.ReactNode;
	location: string;
	product?: string;
}): React.JSX.Element {
	if (!fg('navx-3308-improved-gas-with-error-boundaries')) {
		return <Fragment>{children}</Fragment>;
	}

	return (
		<NavErrorBoundary
			location={`${ERROR_LOCATION__GLOBAL_SHORTCUTS}--${location}`}
			showFlags
			sentryConfig={{ product }}
		>
			{children}
		</NavErrorBoundary>
	);
});
